import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AuthPageLayout, AuthForm } from '../../components/layout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { validateEmail, validateUsername, validatePassword } from '../../utils/validation';
import { RegisterRequest } from '../../services/auth';
import { pageStyles } from '../../config/theme';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated, isLoading, error, clearError } = useAuth();
  
  const [formData, setFormData] = useState<RegisterRequest>(() => {
    // Try to restore form data from sessionStorage if available
    const savedFormData = sessionStorage.getItem('registerFormData');
    if (savedFormData) {
      try {
        const parsed = JSON.parse(savedFormData);
        return parsed;
      } catch (error) {
        console.warn('Failed to parse saved form data:', error);
      }
    }
    return {
      email: '',
      username: '',
      password: '',
    };
  });
  
  const [confirmPassword, setConfirmPassword] = useState(() => {
    // Try to restore confirmPassword from sessionStorage if available
    const savedConfirmPassword = sessionStorage.getItem('registerConfirmPassword');
    if (savedConfirmPassword) {
      return savedConfirmPassword;
    }
    return '';
  });
  const [formErrors, setFormErrors] = useState<Partial<RegisterRequest & { confirmPassword: string }>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const userTypingRef = useRef(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      // Clear sessionStorage before redirecting
      sessionStorage.removeItem('registerFormData');
      sessionStorage.removeItem('registerConfirmPassword');
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors when form data changes (but preserve form data)
  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      setFormErrors({});
    }
  }, [formData, confirmPassword]);

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('registerFormData', JSON.stringify(formData));
  }, [formData]);

  // Save confirmPassword to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('registerConfirmPassword', confirmPassword);
  }, [confirmPassword]);

  // Clear server errors when user starts typing (but not during submission)
  useEffect(() => {
    if (error && !isSubmitting && userTypingRef.current) {
      clearError();
    }
    // Reset the typing flag after clearing error
    if (userTypingRef.current) {
      userTypingRef.current = false;
    }
  }, [formData.email, formData.username, formData.password, confirmPassword, error, clearError, isSubmitting]);

  // Clear sessionStorage when user navigates to a different page (not during registration)
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('registerFormData');
      sessionStorage.removeItem('registerConfirmPassword');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const validateForm = (): boolean => {
    const errors: Partial<RegisterRequest & { confirmPassword: string }> = {};

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Username validation
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (!validateUsername(formData.username)) {
      errors.username = 'Username must be between 3 and 20 characters';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      errors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Mark that user is typing
    userTypingRef.current = true;
    
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    isSubmittingRef.current = true;
    
    try {
      await register(formData);
      // Navigation will be handled by useEffect when isAuthenticated changes
      // Clear saved form data on successful registration
      sessionStorage.removeItem('registerFormData');
      sessionStorage.removeItem('registerConfirmPassword');
    } catch (error) {
      // Error is handled by the useAuth hook and displayed via error state
      // Form data is preserved - no need to clear it
      console.error('Registration failed:', error);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  return (
    <AuthPageLayout
      title="Create Account"
      subtitle="Join the adventure and start your journey"
    >
      <AuthForm onSubmit={handleSubmit}>
            {/* Email Input */}
            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              {...(formErrors.email && { error: formErrors.email })}
              placeholder="Enter your email"
              fullWidth
              autoComplete="email"
              autoFocus
              disabled={isSubmitting || isLoading}
              className="bg-white/90"
            />

            {/* Username Input */}
            <Input
              label="Username"
              name="username"
              type="text"
              value={formData.username}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              {...(formErrors.username && { error: formErrors.username })}
              placeholder="Choose a username"
              fullWidth
              autoComplete="username"
              disabled={isSubmitting || isLoading}
              className="bg-white/90"
            />

            {/* Password Input */}
            <Input
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              {...(formErrors.password && { error: formErrors.password })}
              placeholder="Create a password"
              fullWidth
              autoComplete="new-password"
              disabled={isSubmitting || isLoading}
              className="bg-white/90"
            />

            {/* Confirm Password Input */}
            <Input
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              {...(formErrors.confirmPassword && { error: formErrors.confirmPassword })}
              placeholder="Confirm your password"
              fullWidth
              autoComplete="new-password"
              disabled={isSubmitting || isLoading}
              className="bg-white/90"
            />

            {/* Error Display */}
            {error && (
              <div 
                style={{
                  backgroundColor: '#fed7aa',
                  border: '1px solid #fb923c',
                  borderRadius: '0.375rem',
                  padding: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem' }}>
                  <svg 
                    style={{ height: '1.25rem', width: '1.25rem', color: '#dc2626', marginRight: '0.25rem', flexShrink: 0 }}
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: '700', color: '#dc2626', margin: 0 }}>
                    Registration Failed
                  </h3>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#dc2626', textAlign: 'left' }}>
                  <p style={{ margin: 0 }}>{error.message}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="glass-primary"
              size="lg"
              fullWidth
              loading={isSubmitting || isLoading}
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>

            {/* Links */}
            <div className="text-center">
              <div className="text-sm" style={pageStyles.link.secondary}>
                Already have an account?{' '}
                <Link
                  to="/auth/login"
                  style={pageStyles.link.primary}
                  className="font-medium"
                >
                  Sign in here
                </Link>
              </div>
            </div>
      </AuthForm>
    </AuthPageLayout>
  );
};

export default RegisterPage;
